import React, { useEffect } from "react";
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Clinic } from "./Header";
import { stateOptionswithAbre } from "../../utils/Constants";

interface ClinicOfficeSelectProps {
  clinicOfficeId: string;
  connectedClinics: Clinic[];
  onChange: (event: SelectChangeEvent<string>) => void;
}

const ClinicOfficeSelect: React.FC<ClinicOfficeSelectProps> = ({
  clinicOfficeId,
  connectedClinics,
  onChange,
}) => {
  const theme = useTheme();

  const getStateAbbreviation = (fullName: string | undefined) => {
    if (!fullName) return "N/A"; // Handle undefined state case
    const state = stateOptionswithAbre.find((s) => s.label === fullName);
    return state ? state.value : fullName; // Return abbreviation if found, else return fullName
  };

  const isValidId = connectedClinics.some(
    (clinic) =>
      `clinic-${clinic.id}` === clinicOfficeId ||
      clinic.clinic_offices.some(
        (office) => `office-${office.pkid}` === clinicOfficeId
      )
  );

  useEffect(() => {
    if (!isValidId && clinicOfficeId !== "all") {
      onChange({ target: { value: "all" } } as SelectChangeEvent<string>);
    }
  }, [clinicOfficeId, connectedClinics, isValidId, onChange]);

  const validClinicOfficeId = isValidId ? clinicOfficeId : "all";

  return (
    <FormControl
      fullWidth
      variant="outlined"
      sx={{
        maxWidth: { xs: "100%", sm: 300 },
        marginRight: { xs: 1, sm: 2 },
        backgroundColor: "white",
        borderRadius: 1,
        ".MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "grey.400",
          },
          "&:hover fieldset": {
            borderColor: "grey.600",
          },
          "&.Mui-focused fieldset": {
            borderColor: "primary.main",
          },
        },
      }}
    >
      {connectedClinics.length > 0 && (
        <Select
          labelId="clinic-office-label"
          id="clinic-office-select"
          value={validClinicOfficeId}
          onChange={onChange}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
                minWidth: 300, // Prevents dropdown from overflowing
                overflowY: "auto",
              },
            },
          }}
          sx={{
            "& .Mui-selected": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <MenuItem value="all">All</MenuItem>
          {connectedClinics.map((clinic) => (
            <MenuItem
              key={`clinic-${clinic.id}`}
              value={`clinic-${clinic.id}`}
              sx={{
                fontWeight:
                  validClinicOfficeId === `clinic-${clinic.id}`
                    ? "bold"
                    : "normal",
                backgroundColor:
                  validClinicOfficeId === `clinic-${clinic.id}`
                    ? theme.palette.action.selected
                    : "inherit",
              }}
            >
              {clinic.name}
            </MenuItem>
          ))}
          {connectedClinics.flatMap((clinic) =>
            clinic.clinic_offices.map((office) => (
              <MenuItem
                key={`office-${office.pkid}`}
                value={`office-${office.pkid}`}
                sx={{
                  pl: 4,
                  fontWeight:
                    validClinicOfficeId === `office-${office.pkid}`
                      ? "bold"
                      : "normal",
                  backgroundColor:
                    validClinicOfficeId === `office-${office.pkid}`
                      ? theme.palette.action.selected
                      : "inherit",
                  textOverflow: "ellipsis", // Prevents overflow
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                └ {office.address}, {office.city},{" "}
                {getStateAbbreviation(office.state ?? "")}
              </MenuItem>
            ))
          )}
        </Select>
      )}
    </FormControl>
  );
};

export default ClinicOfficeSelect;
